import { getConfig, regionConfigs } from "custom-config/region.configs";
import {
  buildCountryPropertyPagePath,
  buildLocationUrlWithOfficeTypeFilter,
  buildPropertyNavCityUrl,
} from "~/components/blocks/Geosearch/locationGeosearch";
import { convertSpacesToDashes } from "./StringHelper";

/**
 * IP geolocation payload from `/api/ip/json/region` (region = short code, regionName = display name).
 * For some countries, property URLs use the short code; elsewhere they use the long name.
 */
export type IpLocationForSearchPath = {
  countryCode?: string;
  region?: string;
  regionName?: string;
  city?: string;
  lat?: number;
  lon?: number;
};

type RegionConfigWithGeo = {
  state?: string;
  city?: string;
  country?: string;
  lat?: number;
  lng?: number;
  latitude?: number;
  longitude?: number;
};

const COUNTRIES_USING_REGION_CODE_IN_SEARCH_PATH = new Set([
  "us",
  "au",
  "za",
]);

export type IpLocationSearchPathSlugs = {
  stateSlug: string;
  citySlug: string;
};

function rawStateSegment(
  location: IpLocationForSearchPath | null | undefined,
): string {
  if (!location) return "";
  const code = location.countryCode?.toLowerCase();
  if (code && COUNTRIES_USING_REGION_CODE_IN_SEARCH_PATH.has(code)) {
    return (location.region ?? "").toLowerCase();
  }
  return (location.regionName ?? "").toLowerCase();
}

/**
 * URL path slugs for IP-based property links (`/{country}/{stateSlug}/{citySlug}`).
 * US, AU, ZA: state from `region` (short code). Others: from `regionName`.
 * City is lowercased then passed through `convertSpacesToDashes`.
 */
export function getSearchPathSlugs(
  location: IpLocationForSearchPath | null | undefined,
): IpLocationSearchPathSlugs {
  const stateSlug = convertSpacesToDashes(rawStateSegment(location));
  const citySlug = convertSpacesToDashes((location?.city ?? "").toLowerCase());
  return { stateSlug, citySlug };
}

/** Normalize IP/ISO country codes to OfficeHub region codes (e.g. GB → uk). */
export function normalizeRegionCode(code?: string): string {
  if (!code) return "";
  const lower = code.toLowerCase();
  return lower === "gb" ? "uk" : lower;
}

/**
 * Build a property search path from an IP geolocation payload.
 * Uses the IP country/state/city directly — never the page region or a US default.
 */
export function buildSearchUrlFromIpLocation(
  location: IpLocationForSearchPath | null | undefined,
): string | null {
  const countryCode = normalizeRegionCode(location?.countryCode);
  if (!countryCode) return null;

  const { stateSlug, citySlug } = getSearchPathSlugs(location);
  if (stateSlug && citySlug) {
    return buildPropertyNavCityUrl(`/${countryCode}`, stateSlug, citySlug);
  }
  return buildCountryPropertyPagePath(countryCode);
}

function getRegionDefaultCoords(
  config: RegionConfigWithGeo,
): { lat?: number; lng?: number } {
  return {
    lat: config.lat ?? config.latitude,
    lng: config.lng ?? config.longitude,
  };
}

/**
 * Builds a property-page URL for the landing region. When the user's IP country matches
 * the landing region, uses their detected state/city only (no lat/lng/zoom).
 * Otherwise falls back to the region config defaults with coordinates, or the
 * country property landing page.
 */
export function getRegionAwareSearchUrl(
  landingRegion: string,
  ipLocation?: IpLocationForSearchPath | null,
  options?: { zoom?: number },
): string {
  const zoom = options?.zoom ?? 15;
  const region = normalizeRegionCode(landingRegion) || "us";
  const regionPrefix = `/${region}`;

  const ipRegion = normalizeRegionCode(ipLocation?.countryCode);
  const regionsMatch = Boolean(ipRegion && ipRegion === region);

  if (regionsMatch && ipLocation) {
    const { stateSlug, citySlug } = getSearchPathSlugs(ipLocation);
    if (!stateSlug || !citySlug) {
      return buildCountryPropertyPagePath(region);
    }
    return buildPropertyNavCityUrl(regionPrefix, stateSlug, citySlug);
  }

  const config = getConfig(region as keyof typeof regionConfigs) as RegionConfigWithGeo;
  const state = config?.state;
  const city = config?.city;
  if (!state || !city) {
    return buildCountryPropertyPagePath(region);
  }

  const { lat, lng } = getRegionDefaultCoords(config);
  return buildPropertyNavCityUrl(regionPrefix, state, city, {
    latitude: lat,
    longitude: lng,
    zoom,
  });
}

function formatLocationLabel(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed.length === 2) return trimmed.toUpperCase();
  return trimmed
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function getStateLabelFromIp(
  region: string,
  ipLocation: IpLocationForSearchPath,
): string {
  if (ipLocation.regionName?.trim()) return ipLocation.regionName.trim();
  if (ipLocation.region?.trim()) {
    return COUNTRIES_USING_REGION_CODE_IN_SEARCH_PATH.has(region)
      ? ipLocation.region.trim().toUpperCase()
      : ipLocation.region.trim();
  }
  return "";
}

/**
 * Location label for workspace nav titles (city → state → country).
 * Returns null when search would fall back to the country's property landing page.
 */
export function getWorkspaceLocationLabel(
  landingRegion: string,
  ipLocation?: IpLocationForSearchPath | null,
): string | null {
  const region = normalizeRegionCode(landingRegion) || "us";
  const config = getConfig(
    region as keyof typeof regionConfigs,
  ) as RegionConfigWithGeo;
  const ipRegion = normalizeRegionCode(ipLocation?.countryCode);
  const regionsMatch = Boolean(ipRegion && ipRegion === region);

  if (regionsMatch && ipLocation) {
    const { stateSlug, citySlug } = getSearchPathSlugs(ipLocation);
    if (!stateSlug || !citySlug) return null;

    if (ipLocation.city?.trim()) {
      return formatLocationLabel(ipLocation.city);
    }

    const stateLabel = getStateLabelFromIp(region, ipLocation);
    if (stateLabel) return formatLocationLabel(stateLabel);

    if (config.country) return config.country;
    return null;
  }

  if (!config?.state || !config?.city) return null;

  if (config.city) return formatLocationLabel(config.city);
  if (config.state) return formatLocationLabel(config.state);
  if (config.country) return config.country;
  return null;
}

export function getWorkspaceSearchUrl(
  searchUrl: string,
  workspaceType: string,
): string {
  const basePath = searchUrl.split("?")[0];

  return buildLocationUrlWithOfficeTypeFilter(
    basePath,
    "",
    [workspaceType],
  );
}